/**
 * ルールの詳細。デモの 3 段（直す / 試して見比べる / 決める）をそのまま持ってくる。
 *
 * この画面が守っていること:
 * - **書き込みは全部 `expectedVersion` を添える。** 断られたら（CONFLICT）
 *   上書きせず、読み直してからやり直す導線を出す
 * - **run / stop には確認を挟む。** 公開に直結する唯一のスイッチで、
 *   押し間違いが配信の事故になる（デモには確認が無い）。押せるかどうかは
 *   役割とテナント設定（`runStopPolicy`）がサーバー側で決めるので、
 *   画面は判定を持たず、断られた理由をそのまま出す
 * - **数えていない件数を 0 と書かない。** ルール単位の生成数・配信数は API に無い
 */

import { useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  RuleDocument,
  SaveRuleVersionDocument,
  SetPublishedPromptVersionDocument,
  SetRuleStateDocument,
  Tag,
  UpdateRuleDraftDocument,
  formatJstDateTime,
  type RuleDetailFieldsFragment,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  RULE_COUNTS_NOT_MEASURED,
  RULE_COUNTS_NOT_MEASURED_NOTE,
  RUN_STOP_FORBIDDEN_NOTE,
} from "../api-capabilities";
import { ruleVersionDisplayName } from "../rule-version-name";
import { RuleDryRunPanel } from "./RuleDryRunPanel";
import { RuleFaqGenerationPanel } from "./RuleFaqGenerationPanel";
import { RuleErrors, RuleStateTag, type RuleUserError } from "./RuleParts";

type Step = "edit" | "tryout" | "decide";

const STEPS: readonly { id: Step; label: string }[] = [
  { id: "edit", label: "ルールを直す" },
  { id: "tryout", label: "試して見比べる" },
  { id: "decide", label: "どうするか決める" },
];

/** 版の名前の既定。押した時刻から作る（人に考えさせない） */
function defaultVersionName(now: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `v${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(
    now.getHours(),
  )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

function lines(value: string): string[] {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** 直す段。下書きに溜めるだけで、この時点ではまだ版にならない */
function EditStep({
  rule,
  onSaved,
}: {
  rule: RuleDetailFieldsFragment;
  onSaved: () => void;
}) {
  const draft = rule.draft;
  /**
   * 下書きが無いときは、**配信中の版の本文から始める**。
   * 空欄から書かせると、いま配信している指示を別の画面で開いて
   * 写すことになり、写し間違いがそのまま配信される。
   */
  const publishedBody =
    rule.promptVersions.edges.find((edge) => edge.node.isPublished)?.node
      .promptBody ?? "";
  const [promptBody, setPromptBody] = useState(
    draft?.promptBody ?? publishedBody,
  );
  const [maxPerProduct, setMaxPerProduct] = useState(
    String(draft?.maxPerProduct ?? rule.maxPerProduct),
  );
  const [minEvidenceCount, setMinEvidenceCount] = useState(
    String(draft?.minEvidenceCount ?? rule.minEvidenceCount ?? ""),
  );
  const [answerMaxChars, setAnswerMaxChars] = useState(
    String(draft?.answerMaxChars ?? rule.answerMaxChars ?? ""),
  );
  const [banned, setBanned] = useState(
    (draft?.banned ?? rule.banned).join("\n"),
  );
  const [scope, setScope] = useState(draft?.scope ?? rule.scope ?? "");
  const [userErrors, setUserErrors] = useState<RuleUserError[]>([]);
  const [state, updateRuleDraft] = useMutation(UpdateRuleDraftDocument);

  const submit = (): void => {
    void updateRuleDraft({
      input: {
        ruleId: rule.id,
        expectedVersion: rule.version,
        promptBody,
        maxPerProduct: optionalNumber(maxPerProduct) ?? rule.maxPerProduct,
        minEvidenceCount: optionalNumber(minEvidenceCount),
        answerMaxChars: optionalNumber(answerMaxChars),
        banned: lines(banned),
        scope: scope.trim() === "" ? null : scope.trim(),
      },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setUserErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `保存できませんでした: ${mutationResult.error.message}`,
          },
        ]);
        return;
      }
      const payload = mutationResult.data?.updateRuleDraft;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setUserErrors([...payload.userErrors]);
        return;
      }
      setUserErrors([]);
      onSaved();
    });
  };

  return (
    <Card
      title="ルールを直す"
      extra={<HelpIcon slug="rules/detail" anchor="ルールを直す" />}
      note="ここで直したものは下書きに溜まります。配信されるのは、版として保存して配信する版に決めたものだけです。"
    >
      <div className="vd-stack">
        <RuleErrors
          title="下書きを保存できませんでした"
          errors={userErrors}
          onReload={onSaved}
        />
        {/*
          **保存できたことは、保存した瞬間の合図ではなく下書きの有無で出す。**
          保存すると版が上がって画面が読み直すので、一度きりの合図は
          読み直しと同時に消える(押した人には何も起きなかったように見える)
        */}
        {draft === null || draft === undefined ? null : (
          <p className="vd-stat-note" role="status" style={{ margin: 0 }}>
            下書きがあります（{formatJstDateTime(draft.updatedAt)} に{" "}
            {draft.updatedBy.displayName} が更新）。まだ配信はされていません。
          </p>
        )}

        <div className="vd-field">
          <label htmlFor="rule-prompt-body">プロンプト</label>
          <span className="vd-field-hint">
            JSON
            の形・根拠の照合・件数と字数の上限は、ここで外せません（生成器が
            必ず被せます）
          </span>
          {draft === null || draft === undefined ? (
            publishedBody === "" ? null : (
              <span className="vd-field-hint">
                下書きが無いので、配信中の版の本文から始めています
              </span>
            )
          ) : null}
          <textarea
            id="rule-prompt-body"
            className="vd-input"
            rows={8}
            value={promptBody}
            onChange={(event) => setPromptBody(event.target.value)}
          />
        </div>

        <div className="vd-field">
          <label htmlFor="rule-max-per-product">1 商品あたりの答えの上限</label>
          <input
            id="rule-max-per-product"
            className="vd-input"
            type="number"
            min={1}
            value={maxPerProduct}
            onChange={(event) => setMaxPerProduct(event.target.value)}
          />
        </div>

        <div className="vd-field">
          <label htmlFor="rule-min-evidence">根拠の最低件数</label>
          <span className="vd-field-hint">空欄なら絞りません</span>
          <input
            id="rule-min-evidence"
            className="vd-input"
            type="number"
            min={0}
            value={minEvidenceCount}
            onChange={(event) => setMinEvidenceCount(event.target.value)}
          />
        </div>

        <div className="vd-field">
          <label htmlFor="rule-answer-max-chars">答えの字数の上限</label>
          <span className="vd-field-hint">
            空欄なら生成器の既定。超えた答えは切らずに捨てます
          </span>
          <input
            id="rule-answer-max-chars"
            className="vd-input"
            type="number"
            min={1}
            value={answerMaxChars}
            onChange={(event) => setAnswerMaxChars(event.target.value)}
          />
        </div>

        <div className="vd-field">
          <label htmlFor="rule-banned">禁止表現</label>
          <span className="vd-field-hint">1 行に 1 つ</span>
          <textarea
            id="rule-banned"
            className="vd-input"
            rows={3}
            value={banned}
            onChange={(event) => setBanned(event.target.value)}
          />
        </div>

        <div className="vd-field">
          <label htmlFor="rule-scope">対象の商品群</label>
          <span className="vd-field-hint">
            いまは覚えておくだけで、商品の絞り込みには使っていません
          </span>
          <input
            id="rule-scope"
            className="vd-input"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
          />
        </div>

        <div className="vd-row">
          <Button variant="primary" disabled={state.fetching} onClick={submit}>
            {state.fetching ? "保存しています" : "下書きを保存する"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** 決める段。版として保存 → 配信する版を決める → 配信を入り切りする */
function DecideStep({
  rule,
  onChanged,
}: {
  rule: RuleDetailFieldsFragment;
  onChanged: () => void;
}) {
  const [versionName, setVersionName] = useState(() =>
    defaultVersionName(new Date()),
  );
  const [saveErrors, setSaveErrors] = useState<RuleUserError[]>([]);
  const [publishErrors, setPublishErrors] = useState<RuleUserError[]>([]);
  const [stateErrors, setStateErrors] = useState<RuleUserError[]>([]);
  const [confirmingState, setConfirmingState] = useState<"RUN" | "STOP" | null>(
    null,
  );
  const [saveState, saveRuleVersion] = useMutation(SaveRuleVersionDocument);
  const [publishState, setPublishedPromptVersion] = useMutation(
    SetPublishedPromptVersionDocument,
  );
  const [setStateState, setRuleState] = useMutation(SetRuleStateDocument);

  const draft = rule.draft;
  const versions = rule.promptVersions.edges.map((edge) => edge.node);
  const publishedVersion =
    versions.find((version) => version.isPublished) ?? null;
  const publishedLabel =
    rule.publishedPromptVersion === null
      ? null
      : ruleVersionDisplayName(
          rule.id,
          rule.publishedPromptVersion,
          publishedVersion?.savedAt ?? null,
          formatJstDateTime,
        );

  const save = (): void => {
    if (draft === null || draft === undefined) {
      return;
    }
    void saveRuleVersion({
      input: {
        ruleId: rule.id,
        expectedVersion: rule.version,
        promptVersion: versionName.trim(),
        promptBody: draft.promptBody,
        maxPerProduct: draft.maxPerProduct,
        minEvidenceCount: draft.minEvidenceCount,
        answerMaxChars: draft.answerMaxChars,
        banned: [...draft.banned],
        scope: draft.scope,
      },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setSaveErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `版を保存できませんでした: ${mutationResult.error.message}`,
          },
        ]);
        return;
      }
      const payload = mutationResult.data?.saveRuleVersion;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setSaveErrors([...payload.userErrors]);
        return;
      }
      setSaveErrors([]);
      setVersionName(defaultVersionName(new Date()));
      onChanged();
    });
  };

  const publish = (promptVersion: string): void => {
    void setPublishedPromptVersion({
      input: { ruleId: rule.id, promptVersion, expectedVersion: rule.version },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setPublishErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `配信する版を変えられませんでした: ${mutationResult.error.message}`,
          },
        ]);
        return;
      }
      const payload = mutationResult.data?.setPublishedPromptVersion;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setPublishErrors([...payload.userErrors]);
        return;
      }
      setPublishErrors([]);
      onChanged();
    });
  };

  const switchState = (next: "RUN" | "STOP"): void => {
    void setRuleState({
      input: { ruleId: rule.id, state: next, expectedVersion: rule.version },
    }).then((mutationResult) => {
      if (mutationResult.error !== undefined) {
        setStateErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `切り替えられませんでした: ${mutationResult.error.message}`,
          },
        ]);
        setConfirmingState(null);
        return;
      }
      const payload = mutationResult.data?.setRuleState;
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setStateErrors([...payload.userErrors]);
        setConfirmingState(null);
        return;
      }
      setStateErrors([]);
      setConfirmingState(null);
      onChanged();
    });
  };

  return (
    <div className="vd-stack">
      <Card
        title="版として保存する"
        extra={<HelpIcon slug="rules/detail" />}
        note="版は消せません。どの版で作った答えかを後から辿るために、保存した版はそのまま残ります。"
      >
        <div className="vd-stack">
          <RuleErrors
            title="版を保存できませんでした"
            errors={saveErrors}
            onReload={onChanged}
          />
          {draft === null || draft === undefined ? (
            <p style={{ margin: 0 }}>
              下書きがありません。「ルールを直す」で書いてから版にします。
            </p>
          ) : (
            <>
              <div className="vd-field">
                <label htmlFor="rule-version-name">版の名前</label>
                <span className="vd-field-hint">
                  押した時刻から作った名前が入っています。変えても構いません
                </span>
                <input
                  id="rule-version-name"
                  className="vd-input"
                  value={versionName}
                  onChange={(event) => setVersionName(event.target.value)}
                />
              </div>
              <p className="vd-stat-note" style={{ margin: 0 }}>
                いまの下書き（{draft.promptVersion} ・{" "}
                {formatJstDateTime(draft.updatedAt)} に{" "}
                {draft.updatedBy.displayName} が更新）を版にします。
              </p>
              <div className="vd-row">
                <Button
                  variant="primary"
                  disabled={saveState.fetching || versionName.trim() === ""}
                  onClick={save}
                >
                  {saveState.fetching ? "保存しています" : "版として保存する"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card
        title="配信する版を決める"
        extra={<HelpIcon slug="rules/detail" />}
        note="配信する版を変えても、答えは作り直しません。作り直すときは下の「このルールで答えを作る」を使います。"
      >
        <div className="vd-stack">
          <RuleErrors
            title="配信する版を変えられませんでした"
            errors={publishErrors}
            onReload={onChanged}
          />
          {versions.length === 0 ? (
            <p style={{ margin: 0 }}>保存した版がまだありません。</p>
          ) : (
            <ul className="vd-job-reasons">
              {versions.map((version) => (
                <li key={version.promptVersion}>
                  <div className="vd-row">
                    <span>
                      {ruleVersionDisplayName(
                        rule.id,
                        version.promptVersion,
                        null,
                        formatJstDateTime,
                      )}
                    </span>
                    {version.isPublished ? <Tag tone="ok">配信中</Tag> : null}
                    <span className="vd-stat-note">
                      {formatJstDateTime(version.savedAt)} ・{" "}
                      {version.savedBy.displayName}
                    </span>
                    {version.isPublished ? null : (
                      <Button
                        disabled={publishState.fetching}
                        aria-label={`版 ${version.promptVersion} を配信する版にする`}
                        onClick={() => publish(version.promptVersion)}
                      >
                        この版を配信する
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {rule.promptVersions.pageInfo.hasNextPage ? (
            <p className="vd-stat-note" style={{ margin: 0 }}>
              古い版は省いています（新しいものから 20 件）。
            </p>
          ) : null}
        </div>
      </Card>

      <Card
        title="配信を切り替える"
        extra={<HelpIcon slug="rules/detail" />}
        note="配信されるのは、配信中（RUN）のルールが、配信中の版で作った答えだけです。"
      >
        <div className="vd-stack">
          <RuleErrors
            title="切り替えられませんでした"
            errors={stateErrors}
            onReload={onChanged}
          />
          {stateErrors.some((error) => error.code === "FORBIDDEN") ? (
            <p className="vd-stat-note" style={{ margin: 0 }}>
              {RUN_STOP_FORBIDDEN_NOTE}
            </p>
          ) : null}

          <p style={{ margin: 0 }}>
            いまは
            {rule.state === "RUN"
              ? "配信中です。止めると、このルールが作った答えは配信されなくなります。"
              : "止まっています。配信すると、配信中の版で作った答えが公開されます。"}
          </p>

          {confirmingState === null ? (
            <div className="vd-row">
              {rule.state === "RUN" ? (
                <Button onClick={() => setConfirmingState("STOP")}>
                  配信を止める
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={rule.publishedPromptVersion === null}
                  onClick={() => setConfirmingState("RUN")}
                >
                  配信する
                </Button>
              )}
              {rule.publishedPromptVersion === null ? (
                <span className="vd-stat-note">
                  配信中の版が無いルールは配信にできません。
                </span>
              ) : null}
            </div>
          ) : (
            <div className="vd-notice" role="alert">
              <p style={{ marginTop: 0 }}>
                {confirmingState === "RUN"
                  ? `「${rule.name}」を配信します。配信中の版（${publishedLabel ?? "—"}）で作った答えが、この会社の公開 API から読めるようになります。`
                  : `「${rule.name}」の配信を止めます。このルールが作った答えは公開 API から読めなくなります。`}
              </p>
              <div className="vd-row">
                <Button
                  variant="primary"
                  disabled={setStateState.fetching}
                  onClick={() => switchState(confirmingState)}
                >
                  {confirmingState === "RUN" ? "配信を始める" : "止める"}
                </Button>
                <Button
                  disabled={setStateState.fetching}
                  onClick={() => setConfirmingState(null)}
                >
                  やめる
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <RuleFaqGenerationPanel rule={rule} />
    </div>
  );
}

export function RuleDetailPage() {
  const { ruleId } = useParams();
  const [step, setStep] = useState<Step>("edit");
  const [result, refetch] = useQuery({
    query: RuleDocument,
    variables: { id: ruleId ?? "" },
    pause: ruleId === undefined,
  });
  const reload = (): void => {
    refetch({ requestPolicy: "network-only" });
  };

  if (result.error !== undefined) {
    return (
      <div className="vd-page">
        <ErrorState description={result.error.message} onRetry={reload} />
      </div>
    );
  }
  if (result.fetching && result.data === undefined) {
    return (
      <div className="vd-page">
        <LoadingState />
      </div>
    );
  }
  const rule = result.data?.rule ?? null;
  if (rule === null) {
    return (
      <div className="vd-page">
        <EmptyState title="このルールは見つかりませんでした" />
      </div>
    );
  }

  const publishedLabel =
    rule.publishedPromptVersion === null
      ? null
      : ruleVersionDisplayName(
          rule.id,
          rule.publishedPromptVersion,
          rule.promptVersions.edges.find((edge) => edge.node.isPublished)?.node
            .savedAt ?? null,
          formatJstDateTime,
        );

  return (
    <div className="vd-page">
      <PageHeader
        title={
          <>
            {rule.name}
            <RuleStateTag state={rule.state} />
          </>
        }
        description={rule.description}
        titleExtra={<HelpIcon slug="rules/detail" />}
      />

      <div className="vd-stack">
        <Card title="いまのルール" extra={<HelpIcon slug="rules/detail" />}>
          <div className="vd-grid">
            <div>
              <div className="vd-stat-label">配信中の版</div>
              <div className="vd-stat-value">
                {publishedLabel ?? "まだありません"}
              </div>
            </div>
            <div>
              <div className="vd-stat-label">1 商品の枠</div>
              <div className="vd-stat-value">{rule.maxPerProduct} 件</div>
            </div>
            <div>
              <div className="vd-stat-label">根拠の最低件数</div>
              <div className="vd-stat-value">
                {rule.minEvidenceCount ?? "絞らない"}
              </div>
            </div>
            <div>
              <div className="vd-stat-label">
                生成した答え / 配信している答え
              </div>
              <div className="vd-stat-value">{RULE_COUNTS_NOT_MEASURED}</div>
              <div className="vd-stat-note">
                {RULE_COUNTS_NOT_MEASURED_NOTE}
              </div>
            </div>
          </div>
          {rule.questions.length === 0 ? null : (
            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              作りたい答えの種類: {rule.questions.join(" / ")}
            </p>
          )}
        </Card>

        <div className="vd-row" role="group" aria-label="ルールの進め方">
          {STEPS.map((item) => (
            <Button
              key={item.id}
              variant={step === item.id ? "primary" : "default"}
              aria-pressed={step === item.id}
              onClick={() => setStep(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>

        {step === "edit" ? (
          <EditStep
            // 読み直したら入力欄も最新の値から始める
            key={`${rule.id}-${rule.version}`}
            rule={rule}
            onSaved={reload}
          />
        ) : step === "tryout" ? (
          <RuleDryRunPanel rule={rule} />
        ) : (
          <DecideStep rule={rule} onChanged={reload} />
        )}
      </div>
    </div>
  );
}
